/**
 * DataGridVariant — animated chart lines + counters. Modern dashboard.
 */
import { motion } from "framer-motion";
import Wordmark from "./Wordmark";
import { COPY } from "./copy";

function GridBg() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-25" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(168,85,247,0.35)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
}

export default function DataGridVariant({ portrait }) {
  const headlineSize = portrait ? "text-[110px]" : "text-[100px]";
  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#050816" }}>
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(124,58,237,0.35) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(56,189,248,0.25) 0%, transparent 60%)",
        }}
      />
      <GridBg />

      <svg className="absolute inset-x-0 bottom-0 w-full h-[55%]" viewBox="0 0 1080 600" preserveAspectRatio="none">
        <defs>
          <linearGradient id="weightGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          <linearGradient id="energyGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
          <linearGradient id="weightFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(168,85,247,0.35)" />
            <stop offset="100%" stopColor="rgba(168,85,247,0)" />
          </linearGradient>
        </defs>
        <motion.path
          d="M 0,150 C 200,170 350,220 540,300 C 740,370 880,440 1080,500"
          fill="none" stroke="url(#weightGrad)" strokeWidth="6" strokeLinecap="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 2.4, delay: 0.6, ease: "easeInOut" }}
        />
        <motion.path
          d="M 0,150 C 200,170 350,220 540,300 C 740,370 880,440 1080,500 L 1080,600 L 0,600 Z"
          fill="url(#weightFill)"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 2.6 }}
        />
        <motion.path
          d="M 0,500 C 220,470 380,400 540,300 C 720,200 880,160 1080,120"
          fill="none" stroke="url(#energyGrad)" strokeWidth="6" strokeLinecap="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 2.4, delay: 1.0, ease: "easeInOut" }}
        />
      </svg>

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.6, duration: 0.6 }}
        className="absolute right-12 top-1/3 px-5 py-3 rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 text-white"
      >
        <div className="text-xs uppercase tracking-widest text-purple-200/80">Weight</div>
        <div className="text-3xl font-bold">−14.2 lb</div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.0, duration: 0.6 }}
        className="absolute left-12 top-1/2 px-5 py-3 rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 text-white"
      >
        <div className="text-xs uppercase tracking-widest text-emerald-200/80">Energy</div>
        <div className="text-3xl font-bold">+38%</div>
      </motion.div>

      <div className="absolute inset-0 flex flex-col items-start justify-start text-left text-white px-16 pt-16">
        <Wordmark
          size={portrait ? "xl" : "lg"}
          glpColor="#FFFFFF"
          floClass="bg-gradient-to-r from-cyan-300 to-fuchsia-300 bg-clip-text text-transparent"
          align="left"
          delay={0.1}
        />
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-[14px] tracking-[0.4em] font-bold text-cyan-200/90 mt-2 mb-6 uppercase"
        >
          Companion App
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className={`${headlineSize} font-extrabold leading-[0.9] tracking-tight`}
        >
          {COPY.headline1}
          <br />
          <span className="bg-gradient-to-r from-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">{COPY.headline2}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.6 }}
          className="text-2xl text-purple-100/90 mt-4 font-medium"
        >
          {COPY.sub}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 3.4, duration: 0.5 }}
        className="absolute bottom-10 left-0 right-0 text-center"
      >
        <p className="text-sm tracking-[0.3em] uppercase text-cyan-200/80">
          {COPY.footer} ·{" "}<span className="text-white font-bold">{COPY.url}</span>
        </p>
      </motion.div>
    </div>
  );
}
